"use strict";
import { memo, useState } from "react";
import { Flex, Text, UniversalImage, useColorSchemeFromSeed, useSporeColors } from "ui/src";
import { iconSizes, zIndexes } from "ui/src/theme";
import { STATUS_RATIO } from "components/CurrencyLogo/CurrencyLogo";
import { NetworkLogo } from "components/CurrencyLogo/NetworkLogo";
import { UniverseChainId } from "features/chains/types";
import { isTestnetChain } from "features/chains/utils";
import { isMobileApp } from "utilities/src/platform";
export const TokenLogo = memo(function _TokenLogo({
  url,
  symbol,
  name,
  chainId,
  size = iconSizes.icon32,
  hideNetworkLogo,
  networkLogoBorderWidth = isMobileApp ? 2 : 1.5,
  loading
}) {
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const _colors = useSporeColors();
  const { foreground: _foreground, background: _background } = useColorSchemeFromSeed(name ?? symbol ?? "");
  const _isTestnetToken = chainId && isTestnetChain(chainId);
  const showNetworkLogo = !hideNetworkLogo && chainId && chainId !== UniverseChainId.Mainnet;
  const networkLogoSize = Math.round(size * STATUS_RATIO);
  const showDashedBorder = !url || imageLoadFailed;
  const borderWidth = showDashedBorder ? size / 15 : 0;
  const borderOffset = showDashedBorder ? 4 : 0;
  const tokenSize = size - borderWidth - borderOffset;
  if (loading) {
    return <Flex
      className="topaz-shimmer"
      borderRadius="$roundedFull"
      height={size}
      width={size}
    />;
  }
  const fallback = <Flex
    alignItems="center"
    borderRadius="$roundedFull"
    height={tokenSize}
    justifyContent="center"
    px="$spacing8"
    backgroundColor="$accent2"
    width={tokenSize}
  >
      <Text
    adjustsFontSizeToFit
    $platform-web={{
      // adjustFontSizeToFit is a react-native-only prop
      fontSize: 10,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "clip"
    }}
    allowFontScaling={false}
    color="$accent1"
    fontFamily="$button"
    fontSize={17}
    fontWeight="600"
    lineHeight={14}
    minimumFontScale={0.5}
    numberOfLines={1}
  >
        {symbol?.slice(0, 3)}
      </Text>
    </Flex>;
  const tokenImage = <UniversalImage
    allowLocalUri
    fallback={fallback}
    size={{ height: tokenSize, width: tokenSize }}
    style={{
      image: {
        borderRadius: size / 2,
        zIndex: zIndexes.default
      }
    }}
    testID="token-image"
    uri={url ?? void 0}
    onLoad={() => setImageLoadFailed(false)}
    onError={() => setImageLoadFailed(true)}
  />;
  return <Flex
    alignItems="center"
    height={size}
    justifyContent="center"
    testID="token-logo"
    pointerEvents="auto"
    width={size}
    position="relative"
  >
      {tokenImage}
      {showDashedBorder && <Flex
    borderRadius={size / 2}
    borderStyle="dashed"
    borderColor="$accent2"
    borderWidth={borderWidth}
    height={size}
    width={size}
    style={{ boxSizing: "border-box" }}
    position="absolute"
  />}
      {showNetworkLogo && <Flex bottom={-2} position="absolute" right={-3} zIndex={zIndexes.mask}>
          <NetworkLogo borderWidth={networkLogoBorderWidth} chainId={chainId} size={networkLogoSize} />
        </Flex>}
    </Flex>;
});
